import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, FileText, Download, Loader2, AlertTriangle, CheckCircle, Calendar, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Police logo URL
const POLICE_LOGO_URL = 'https://customer-assets.emergentagent.com/job_1edf88dc-3fd5-40c7-b1aa-85fddbf9a215/artifacts/0qau0qow_Politiet_logo_sort.png';

const WeaponLoanView = () => {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isOrgAdmin, isRangeOfficer } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [loan, setLoan] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  const hasAccess = isAdmin || isOrgAdmin || isRangeOfficer;

  useEffect(() => {
    const loadLoan = async () => {
      if (!hasAccess || !loanId) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE}/api/club-operations/weapons/loan/${loanId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLoan(response.data);
      } catch (error) {
        console.error('Error loading loan:', error);
        toast.error('Kunne ikke laste utlånserklæring');
      } finally {
        setLoading(false);
      }
    };
    
    loadLoan();
  }, [loanId, hasAccess]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/weapons/loan/${loanId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Utlanserklaring_${loan?.borrower?.name?.replace(/\s+/g, '_') || 'unknown'}_${loan?.loan_from_date || 'date'}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF lastet ned');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Kunne ikke laste ned PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleReturnLoan = async () => {
    if (!window.confirm('Er du sikker på at du vil markere dette utlånet som returnert?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/api/club-operations/weapons/loan/${loanId}/return`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reload loan data
      const response = await axios.get(`${API_BASE}/api/club-operations/weapons/loan/${loanId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoan(response.data);
      
      toast.success('Utlån markert som returnert. Våpenet er nå tilgjengelig igjen.');
      // Navigate back to weapons list so admin sees the updated tag immediately
      setTimeout(() => {
        window.location.href = '/club-operations';
      }, 900);
    } catch (error) {
      console.error('Error returning loan:', error);
      toast.error('Kunne ikke markere som returnert');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('nb-NO', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-8 max-w-md text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Ingen tilgang</h2>
          <p className="text-gray-600 mb-4">Du har ikke tilgang til denne siden.</p>
          <a href="/home" className="text-blue-600 hover:underline">Tilbake til hjem</a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-8 max-w-md text-center">
          <AlertTriangle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Utlån ikke funnet</h2>
          <p className="text-gray-600 mb-4">Kunne ikke finne utlånserklæringen.</p>
          <a href="/club-operations" className="text-blue-600 hover:underline">Tilbake til Klubb drift</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gray-800 p-4 flex items-center justify-between sticky top-0 z-10">
        <a href="/club-operations" className="flex items-center gap-2 text-gray-300 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
          <span>Tilbake</span>
        </a>
        <div className="flex items-center gap-2 text-yellow-500">
          <FileText className="w-5 h-5" />
          <span className="font-bold">Utlånserklæring</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            <span className="hidden sm:inline">Last ned PDF</span>
          </button>
        </div>
      </div>

      {/* Status Banner */}
      {loan.status === 'returned' ? (
        <div className="bg-green-100 border-b border-green-200 px-4 py-3 flex items-center justify-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800 font-medium">
            Returnert {formatDate(loan.returned_at)} av {loan.returned_by_name || 'Ukjent'}
          </span>
        </div>
      ) : (
        <div className="bg-yellow-100 border-b border-yellow-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800 font-medium">
              Aktivt utlån - Gyldig til {formatDate(loan.loan_to_date)}
            </span>
          </div>
          <button
            onClick={handleReturnLoan}
            className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-sm font-medium"
          >
            Marker som returnert
          </button>
        </div>
      )}

      {/* Document Content */}
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden print:shadow-none">
          {/* Police Header */}
          <div className="bg-white p-6 border-b-2 border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <img 
                src={POLICE_LOGO_URL} 
                alt="Politiet" 
                className="h-16 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <h1 className="text-2xl font-bold text-gray-900">Utlånserklæring</h1>
            </div>
            
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                Dette skjema benyttes når det lånes ut våpen jf. våpenloven § 18 første ledd, 
                jf. våpenforskriften (2021) § 6-9.
              </p>
            </div>
          </div>

          {/* Section 1: Lender */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
              Utlåner
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Navn:</span>
                <p className="font-medium">{loan.lender?.name || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Fødselsdato / Org.nr:</span>
                <p className="font-medium">{loan.lender?.birthdate || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Våpenkortnummer:</span>
                <p className="font-medium">{loan.lender?.weapon_card_number || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Utstedt av:</span>
                <p className="font-medium">{loan.lender?.issued_by || '-'}</p>
              </div>
            </div>
          </div>

          {/* Section 2: Borrower */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
              Låntaker
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Navn:</span>
                <p className="font-medium">{loan.borrower?.name || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Fødselsdato:</span>
                <p className="font-medium">{loan.borrower?.birthdate || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Våpenkortnummer:</span>
                <p className="font-medium">{loan.borrower?.weapon_card_number || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Utstedt av:</span>
                <p className="font-medium">{loan.borrower?.issued_by || '-'}</p>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500">Identitetsdokument:</span>
                <p className="font-medium">{loan.borrower?.id_document || '-'}</p>
              </div>
            </div>
          </div>

          {/* Section 3: Weapons */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">3</span>
              Informasjon om våpen
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border">#</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border">Våpentype</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border">Merke</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border">Modell</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border">Kaliber</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border">Serienummer</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border">Funksjonsmåte</th>
                  </tr>
                </thead>
                <tbody>
                  {(loan.weapons || []).map((weapon, index) => (
                    <tr key={index}>
                      <td className="px-3 py-2 border text-center">{index + 1}</td>
                      <td className="px-3 py-2 border">{weapon.weapon_type || '-'}</td>
                      <td className="px-3 py-2 border font-medium">{weapon.make || '-'}</td>
                      <td className="px-3 py-2 border">{weapon.model || '-'}</td>
                      <td className="px-3 py-2 border">{weapon.caliber || '-'}</td>
                      <td className="px-3 py-2 border font-mono">{weapon.serial_number || '-'}</td>
                      <td className="px-3 py-2 border">{weapon.function_mode || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 4: Usage Area */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">4</span>
              Bruksområde
            </h2>
            <p className="text-gray-700">{loan.usage_area || 'Ikke spesifisert'}</p>
          </div>

          {/* Section 5: Duration */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">5</span>
              Utlånets varighet
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Utlånt fra:</span>
                <p className="font-medium">{formatDate(loan.loan_from_date)}</p>
              </div>
              <div>
                <span className="text-gray-500">Utlånt til:</span>
                <p className="font-medium">{formatDate(loan.loan_to_date)}</p>
              </div>
            </div>
          </div>

          {/* Section 6: Signatures */}
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="bg-gray-900 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">6</span>
              Signatur
            </h2>
            <div className="grid grid-cols-2 gap-6">
              <div className="border-t-2 border-gray-900 pt-2">
                <p className="text-xl italic text-gray-800">{loan.lender_signature}</p>
                <p className="text-sm text-gray-500 mt-1">Utlåner</p>
              </div>
              <div className="border-t-2 border-gray-900 pt-2">
                <p className="text-xl italic text-gray-800">{loan.borrower_signature}</p>
                <p className="text-sm text-gray-500 mt-1">Låntaker</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4 text-center">
              Elektronisk signert {formatDate(loan.signed_at)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeaponLoanView;
